import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Printer, Search } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { CngQrSticker } from '../../components/stickers/CngQrSticker';
import { DriverQrCard } from '../../components/stickers/DriverQrCard';
import { DmpLogo } from '../../components/brand/DmpLogo';
import { usePrototype } from '../../contexts/PrototypeContext';
import { getDriver } from '../../data/drivers';
import { qrStatus } from '../../utils/status';
import type { Driver } from '../../types/domain';
export function StickerIssue() {
  const [params] = useSearchParams();
  const {
    cngList,
    setStage
  } = usePrototype();
  const initialId = params.get('id') ?? cngList.find(c => c.stage === 6)?.id ?? 'c1';
  const [cngId, setCngId] = useState(initialId);
  const [q, setQ] = useState(cngList.find(c => c.id === initialId)?.reg ?? '');
  const [notFound, setNotFound] = useState(false);
  const cng = cngList.find(c => c.id === cngId);
  const [form, setForm] = useState({
    date: '১২ মার্চ ২০২৬',
    officer: 'নায়েক মোঃ সাজ্জাদ হোসেন (স্টিকার সেল)',
    receiver: cng?.owner.name ?? '',
    mobile: cng?.owner.mobile ?? '',
    place: `ট্রাফিক-${cng?.division ?? 'মিরপুর'} বিভাগীয় কার্যালয়`
  });
  const [receipt, setReceipt] = useState(false);
  const search = () => {
    const t = q.trim().toLowerCase();
    const r = cngList.find(c => c.reg.includes(q.trim()) || c.qrId.toLowerCase() === t);
    setNotFound(!r);
    if (r) {
      setCngId(r.id);
      setForm(f => ({
        ...f,
        receiver: r.owner.name,
        mobile: r.owner.mobile,
        place: `ট্রাফিক-${r.division} বিভাগীয় কার্যালয়`
      }));
    }
  };
  if (!cng) return null;
  const driverList = cng.driverIds.map(d => getDriver(d)).filter((d): d is Driver => Boolean(d));
  const qr = qrStatus(cng.stage);
  const canIssue = cng.stage === 6;
  return <div data-id="element-1729" data-mp="1emjxxmwcd89q">
      <PageHeader title="স্টিকার ইস্যু" subtitle="প্রিন্টকৃত স্টিকার মালিক/চালকের কাছে হস্তান্তর ও প্রাপ্তিস্বীকার রসিদ" data-id="element-1730" data-mp="1ema7xzbar141" />
      <Panel className="mb-4" data-id="element-1731" data-mp="19n79wytqhv5k">
        <div className="flex flex-wrap items-end gap-3" data-id="element-1732" data-mp="14q766ukcx4hm">
          <div className="min-w-[280px] flex-1" data-id="element-1733" data-mp="14nuh7xg6ltv2">
            <label htmlFor="si-q" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1734" data-mp="zpsw76z73kgy">CNG Registration / QR ID</label>
            <div className="relative" data-id="element-1735" data-mp="vpsgzk49v58u">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-1736" data-mp="qq5vkneaiioy" />
              <Input id="si-q" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} className="pl-9" placeholder="ঢাকা-থ-১৪-১৩৫৭ অথবা CNG-000006" data-id="element-1737" data-mp="2q39usfevknqo" />
            </div>
          </div>
          <Button onClick={search} data-id="element-1738" data-mp="2sohpdzwzn9a2">খুঁজুন</Button>
          <div className="flex items-center gap-2 text-sm" data-id="element-1739" data-mp="2sllp44xo48ig">
            <span className="font-semibold text-navy-900" data-id="element-1740" data-mp="2xlrcva0thav9">{cng.reg}</span>
            <StatusBadge tone={qr.tone} data-id="element-1741" data-mp="32czkq1mi0o6b">{qr.label}</StatusBadge>
          </div>
        </div>
        {notFound && <p className="mt-2 text-xs text-red-600" data-id="element-1742" data-mp="37d41lsrbjkx2">কোনো রেকর্ড পাওয়া যায়নি</p>}
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[1fr_400px]" data-id="element-1743" data-mp="29pdiawp0bpl1">
        <Panel title="ইস্যুযোগ্য স্টিকার" data-id="element-1744" data-mp="29qye0pn25ekh">
          <div className="flex flex-wrap items-start gap-6" data-id="element-1745" data-mp="2enf3bn5ztyz5">
            <div data-id="element-1746" data-mp="2eprsaka659lp">
              <p className="mb-2 text-xs font-semibold text-slate-500" data-id="element-1747" data-mp="2jn9qpgqk86fg">CNG sticker</p>
              <CngQrSticker qrId={cng.qrId} reg={cng.reg} size="sm" data-id="element-1748" data-mp="2jpe20jqsg7pr" />
            </div>
            <div className="flex-1" data-id="element-1749" data-mp="2nmikumpii6bj">
              <p className="mb-2 text-xs font-semibold text-slate-500" data-id="element-1750" data-mp="3v2q2qlr94a7p">Driver sticker(s) — {driverList.length.toLocaleString('bn-BD')}টি</p>
              <div className="flex flex-wrap gap-3" data-id="element-1751" data-mp="2wt76d0joqkf">{driverList.map(d => <DriverQrCard key={d.id} driver={d} vehicleReg={cng.reg} size="sm" data-id="element-1752" data-mp="1gp5581d9qm4" />)}</div>
            </div>
          </div>
        </Panel>
        <Panel title="ইস্যু তথ্য" data-id="element-1753" data-mp="3sp303zanh4ng">
          <div className="space-y-3" data-id="element-1754" data-mp="bfenf1x6dd2w">
            {([['date', 'ইস্যুর তারিখ'], ['officer', 'ইস্যুকারী অফিসার'], ['receiver', 'গ্রহণকারীর নাম'], ['mobile', 'মোবাইল'], ['place', 'ইস্যু স্থান']] as const).map(([k, l]) => <div key={k} data-id="element-1755" data-mp="fh9ny47a7mfm">
                <label htmlFor={`si-${k}`} className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1756" data-mp="ffylc7pkokq3">{l}</label>
                <Input id={`si-${k}`} value={form[k]} onChange={e => setForm(f => ({
              ...f,
              [k]: e.target.value
            }))} data-id="element-1757" data-mp="u4l0a8bafags" />
              </div>)}
            <label className="flex items-start gap-2 text-xs text-slate-600" data-id="element-1758" data-mp="136b1u6yvvpsa">
              <input type="checkbox" defaultChecked className="mt-0.5 accent-navy-800" data-id="element-1759" data-mp="13481ou40og9u" />
              গ্রহণকারীর NID যাচাই ও স্বাক্ষর গ্রহণ করা হয়েছে
            </label>
            {cng.stage < 6 && <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800" data-id="element-1760" data-mp="1hr6qsifeiyle">স্টিকার এখনো প্রিন্ট হয়নি — প্রিন্ট সম্পন্ন হলে ইস্যু করা যাবে।</p>}
            {cng.stage >= 7 && <p className="rounded-md bg-emerald-50 p-2 text-xs text-emerald-800" data-id="element-1761" data-mp="18rjq5yuhxwte">এই সিএনজির স্টিকার ইতোমধ্যে ইস্যুকৃত ও সক্রিয়।</p>}
            <Button className="h-11 w-full bg-bdgreen-600 hover:bg-bdgreen-700" disabled={!canIssue} onClick={() => {
            setStage(cng.id, 7);
            setReceipt(true);
            toast.success(`${cng.reg} — স্টিকার ইস্যু সম্পন্ন, সিএনজি এখন সক্রিয়`);
          }} data-id="element-1762" data-mp="1dtskayj3v4x3">
              <CheckCircle2 className="h-4 w-4" data-id="element-1763" data-mp="u2xb7y8s772p" />স্টিকার ইস্যু সম্পন্ন করুন
            </Button>
            {cng.stage >= 7 && <Button variant="outline" className="w-full" onClick={() => setReceipt(true)} data-id="element-1764" data-mp="g4z0r3xkzush"><Printer className="h-4 w-4" data-id="element-1765" data-mp="l3jxgtdmjwc6" />রসিদ দেখুন</Button>}
          </div>
        </Panel>
      </div>

      <Modal open={receipt} onClose={() => setReceipt(false)} title="স্টিকার ইস্যু রসিদ" footer={<><Button variant="outline" onClick={() => setReceipt(false)} data-id="element-1767" data-mp="2i0ocvnzocphn">বন্ধ করুন</Button><Button onClick={() => window.print()} data-id="element-1768" data-mp="2dz33wdq9gvcq"><Printer className="h-4 w-4" data-id="element-1769" data-mp="2e04c8afw73qr" />প্রিন্ট</Button></>} data-id="element-1766" data-mp="2msg29v73gp67">
        <div className="rounded-md border border-slate-300 p-5" data-id="element-1770" data-mp="291uim49tgb79">
          <div className="flex items-center gap-3 border-b border-slate-200 pb-3" data-id="element-1771" data-mp="35qsczldnm73w">
            <DmpLogo size={44} data-id="element-1772" data-mp="35opo54o8mk0r" />
            <div data-id="element-1773" data-mp="31oxaidjzlnoj">
              <p className="font-semibold text-navy-900" data-id="element-1774" data-mp="31qi5g4a7r8mx">ঢাকা মেট্রোপলিটন পুলিশ — ট্রাফিক বিভাগ</p>
              <p className="text-xs text-slate-500" data-id="element-1775" data-mp="2wpawi1wc43eo">QR স্টিকার ইস্যু প্রাপ্তিস্বীকার রসিদ</p>
            </div>
            <span className="ml-auto font-mono text-xs text-slate-500" data-id="element-1776" data-mp="2rqrci71ax1oy">RCPT-20260312-0418</span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm" data-id="element-1777" data-mp="1lcia76mbc760">
            <dt className="text-slate-500" data-id="element-1778" data-mp="1gbuieiq9fmaq">রেজিস্ট্রেশন</dt><dd className="font-semibold" data-id="element-1779" data-mp="1ge72b1fkw6qf">{cng.reg}</dd>
            <dt className="text-slate-500" data-id="element-1780" data-mp="1698fohht8t0g">গাড়ির QR</dt><dd className="font-mono" data-id="element-1781" data-mp="1662uveg7sdaj">{cng.qrId}</dd>
            <dt className="text-slate-500" data-id="element-1782" data-mp="1b8bo8bx01hd6">চালক QR</dt><dd className="font-mono text-xs" data-id="element-1783" data-mp="1b560b003wlj1">{driverList.map(d => d.qrId).join(', ')}</dd>
            <dt className="text-slate-500" data-id="element-1784" data-mp="1g4a5zgnto14m">ইস্যুর তারিখ</dt><dd data-id="element-1785" data-mp="1kxlcthsmwhwj">{form.date}</dd>
            <dt className="text-slate-500" data-id="element-1786" data-mp="1kymrdw8uzmj0">ইস্যুকারী</dt><dd data-id="element-1787" data-mp="1oyoum9u8so0e">{form.officer}</dd>
            <dt className="text-slate-500" data-id="element-1788" data-mp="1oxn7v83v6d2u">গ্রহণকারী</dt><dd data-id="element-1789" data-mp="1p0rioobu472h">{form.receiver} ({form.mobile})</dd>
            <dt className="text-slate-500" data-id="element-1790" data-mp="1twqdouro4nbv">ইস্যু স্থান</dt><dd data-id="element-1791" data-mp="1tzuoiazn2hbi">{form.place}</dd>
          </dl>
          <p className="mt-4 rounded bg-slate-50 p-2 text-[11px] text-slate-600" data-id="element-1792" data-mp="1ywl5r1n6jiyi">নির্দেশনা: গাড়ির স্টিকারটি সামনের কাচের ভেতরের দিকে বাম কোণে স্থাপন করুন। স্টিকার নষ্ট/হারালে ২৪ ঘণ্টার মধ্যে প্রতিস্থাপনের আবেদন করুন।</p>
          <div className="mt-8 grid grid-cols-2 gap-6 text-center text-xs text-slate-500" data-id="element-1793" data-mp="4k4z3zqh22t4">
            <div className="border-t border-slate-300 pt-1" data-id="element-1794" data-mp="9ipqrarnre5y">গ্রহণকারীর স্বাক্ষর</div>
            <div className="border-t border-slate-300 pt-1" data-id="element-1795" data-mp="2beou1lqj8m2e">ইস্যুকারী অফিসারের স্বাক্ষর ও সিল</div>
          </div>
        </div>
      </Modal>
    </div>;
}